/* eslint-disable react/no-array-index-key */
/* eslint-disable max-len */
import { useState } from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaBriefcase, FaCalendarAlt } from 'react-icons/fa';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

const staggerVariant = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariant = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.4, ease: 'easeOut' } }
};

// Styles
const colors = {
  primary: '#3a86ff',
  secondary: '#ff006e',
  accent: '#8338ec',
  success: '#06d6a0',
  warning: '#ffbe0b',
  dark: '#1e2a3a',
  light: '#f8f9fa',
  darkGradient: 'linear-gradient(135deg, #0b1120 0%, #1e293b 100%)',
  glassEffect: 'rgba(255, 255, 255, 0.03)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
};

const fontFamily = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

const sectionStyle = {
  padding: '120px 24px',
  backgroundColor: colors.light,
  fontFamily,
  position: 'relative',
  overflow: 'hidden',
};

function Recruitment() {
  const [activeJob, setActiveJob] = useState(null);

  // Liste des postes à pourvoir
  const jobs = [
    {
      id: 1,
      title: 'Responsable Commercial',
      location: 'France (Télétravail possible)',
      type: 'CDI',
      date: '10 mai 2025',
      department: 'Commercial',
      shortDescription: 'Développez notre portefeuille clients dans le secteur du transport sanitaire en France.',
      description: `Nous recherchons un Responsable Commercial expérimenté pour développer notre activité dans le secteur du transport sanitaire en France.

Vos missions principales :
- Prospecter et acquérir de nouveaux clients dans le secteur du transport sanitaire
- Élaborer des propositions commerciales adaptées aux besoins des clients
- Assurer le suivi et le développement du portefeuille clients existant
- Participer aux événements professionnels du secteur
- Collaborer avec les équipes produit pour faire remonter les besoins des clients

Profil recherché :
- Professionnel confirmé dans la vente de solutions B2B, idéalement dans le secteur de la santé
- Excellentes capacités de communication et de négociation
- Autonomie et sens de l'initiative
- Connaissance du secteur du transport sanitaire appréciée`,
      requirements: [
        'Minimum 5 ans d\'expérience commerciale B2B',
        'Expérience dans la vente de solutions logicielles SaaS',
        'Connaissance du secteur de la santé',
        'Permis B et disponibilité pour des déplacements réguliers',
        'Capacité à travailler en autonomie'
      ]
    },
    {
      id: 2,
      title: 'Support Technique',
      location: 'Paris / Télétravail',
      type: 'CDI',
      date: '5 mai 2025',
      department: 'Support Client',
      shortDescription: 'Assurez le support technique de nos clients et garantissez la qualité de service de notre solution.',
      description: `Nous recherchons un Support Technique pour accompagner nos clients et résoudre leurs problèmes techniques.

Vos missions principales :
- Assurer le support technique auprès de nos clients
- Diagnostiquer et résoudre les problèmes techniques rencontrés par les utilisateurs
- Documenter les solutions et procédures pour améliorer le support
- Collaborer avec l'équipe de développement pour remonter les bugs et anomalies
- Participer à l'amélioration continue de notre produit

Profil recherché :
- Compétences techniques et capacité à résoudre des problèmes
- Excellent relationnel et sens du service client
- Rigueur et méthode dans le traitement des demandes
- Patience et pédagogie pour accompagner les utilisateurs`,
      requirements: [
        'Formation technique ou expérience équivalente',
        'Connaissance des environnements web et des bases de données',
        'Expérience dans le support client d\'applications SaaS',
        'Excellentes compétences en communication écrite et orale',
        'Capacité à travailler en équipe et à gérer les priorités'
      ]
    }
  ];

  // Valeurs de l'entreprise
  const values = [
    {
      title: 'Innovation',
      description: 'Nous encourageons la créativité et l\'innovation pour résoudre les défis du transport sanitaire'
    },
    {
      title: 'Collaboration',
      description: 'Nous croyons en la puissance du travail d\'équipe pour atteindre nos objectifs'
    },
    {
      title: 'Excellence',
      description: 'Nous visons l\'excellence dans tous les aspects de notre travail'
    },
    {
      title: 'Intégrité',
      description: 'Nous agissons avec honnêteté et transparence dans toutes nos interactions'
    }
  ];

  // Avantages offerts
  const benefits = [
    'Environnement de travail stimulant et innovant',
    'Flexibilité des horaires et possibilité de télétravail',
    'Formation continue et développement professionnel',
    'Équipement informatique de pointe',
    'Tickets restaurant et mutuelle d\'entreprise',
    'Participation aux événements et séminaires d\'équipe'
  ];

  return (
    <div style={{ overflow: 'hidden' }}>
      {/* HERO SECTION */}
      <motion.section
        style={{
          ...sectionStyle,
          background: colors.darkGradient,
          color: colors.light,
          paddingTop: '150px',
          paddingBottom: '100px',
          textAlign: 'center',
          position: 'relative'
        }}
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
      >
        {/* Éléments décoratifs */}
        <div style={{
          position: 'absolute',
          top: '0',
          left: '10%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(58, 134, 255, 0.3) 0%, transparent 70%)',
          filter: 'blur(80px)',
          zIndex: 0,
          opacity: 0.5
        }}
        />

        <div style={{
          position: 'absolute',
          bottom: '0',
          right: '10%',
          width: '250px',
          height: '250px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255, 0, 110, 0.3) 0%, transparent 70%)',
          filter: 'blur(60px)',
          zIndex: 0,
          opacity: 0.5
        }}
        />

        <div style={{
          position: 'relative', zIndex: 1, maxWidth: '900px', margin: '0 auto'
        }}
        >
          <motion.h1
            variants={itemVariant}
            style={{
              fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
              fontWeight: '800',
              marginBottom: '30px',
              background: 'linear-gradient(90deg, #ffffff 0%, #a2d6fc 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.5px',
              lineHeight: '1.2',
            }}
          >
            Rejoignez notre équipe
          </motion.h1>

          <motion.p
            variants={itemVariant}
            style={{
              fontSize: 'clamp(1.1rem, 2vw, 1.4rem)',
              fontWeight: '400',
              maxWidth: '700px',
              margin: '0 auto 50px',
              color: 'rgba(255, 255, 255, 0.85)',
              lineHeight: '1.6',
            }}
          >
            Découvrez nos opportunités de carrière et participez à la transformation du transport sanitaire
          </motion.p>
        </div>
      </motion.section>

      {/* SECTION OFFRES D'EMPLOI */}
      <motion.section
        style={{
          ...sectionStyle,
          background: 'linear-gradient(135deg, #f8f9fa 0%, #f1f5f9 100%)'
        }}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={fadeInUp}
      >
        <div style={{
          position: 'relative', zIndex: 1, maxWidth: '1000px', margin: '0 auto'
        }}
        >
          <motion.h2
            variants={itemVariant}
            style={{
              fontSize: 'clamp(2rem, 3vw, 2.5rem)',
              fontWeight: '800',
              color: colors.dark,
              marginBottom: '20px',
              textAlign: 'center',
              position: 'relative',
              display: 'inline-block',
              width: '100%'
            }}
          >
            Nos opportunités actuelles
            <div style={{
              position: 'absolute',
              bottom: '-10px',
              left: '50%',
              width: '60px',
              height: '4px',
              background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
              borderRadius: '2px',
              transform: 'translateX(-50%)'
            }}
            />
          </motion.h2>

          <motion.p
            variants={itemVariant}
            style={{
              fontSize: 'clamp(1rem, 1.5vw, 1.2rem)',
              color: '#64748b',
              textAlign: 'center',
              maxWidth: '700px',
              margin: '0 auto 60px',
              lineHeight: '1.6',
            }}
          >
            Explorez nos postes ouverts et rejoignez une équipe passionnée par l'innovation dans le secteur de la santé
          </motion.p>

          <motion.div
            variants={staggerVariant}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              marginBottom: '60px'
            }}
          >
            {jobs.map((job) => (
              <motion.div
                key={job.id}
                variants={itemVariant}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.06)',
                  border: '1px solid rgba(0, 0, 0, 0.03)',
                  transition: 'all 0.3s ease',
                }}
                whileHover={{
                  y: -5,
                  boxShadow: '0 15px 35px rgba(0, 0, 0, 0.1)'
                }}
              >
                <div style={{
                  padding: '25px',
                  borderBottom: activeJob === job.id ? '1px solid rgba(0, 0, 0, 0.05)' : 'none'
                }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: '15px'
                  }}
                  >
                    <div style={{ flex: '1 1 300px' }}>
                      <h3 style={{
                        fontSize: '1.3rem',
                        fontWeight: '700',
                        marginBottom: '10px',
                        color: colors.dark,
                        lineHeight: '1.4'
                      }}
                      >
                        {job.title}
                      </h3>

                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '20px',
                        marginBottom: '15px'
                      }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: '0.9rem',
                          color: '#64748b'
                        }}
                        >
                          <FaMapMarkerAlt style={{ marginRight: '8px', fontSize: '0.9rem' }} />
                          {job.location}
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: '0.9rem',
                          color: '#64748b'
                        }}
                        >
                          <FaBriefcase style={{ marginRight: '8px', fontSize: '0.9rem' }} />
                          {job.type}
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: '0.9rem',
                          color: '#64748b'
                        }}
                        >
                          <FaCalendarAlt style={{ marginRight: '8px', fontSize: '0.9rem' }} />
                          Publié le {job.date}
                        </div>
                      </div>

                      <p style={{
                        fontSize: '0.95rem',
                        color: '#475569',
                        lineHeight: '1.7'
                      }}
                      >
                        {job.shortDescription}
                      </p>
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setActiveJob(activeJob === job.id ? null : job.id)}
                      style={{
                        backgroundColor: colors.primary,
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '12px 24px',
                        fontWeight: '600',
                        fontSize: '0.95rem',
                        cursor: 'pointer',
                        boxShadow: '0 8px 15px rgba(58, 134, 255, 0.2)',
                        fontFamily,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {activeJob === job.id ? 'Voir moins' : 'Voir détails'}
                    </motion.button>
                  </div>
                </div>

                {activeJob === job.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{
                      padding: '0 25px 25px',
                      backgroundColor: 'white'
                    }}
                  >
                    <div style={{
                      marginBottom: '20px'
                    }}
                    >
                      <h4 style={{
                        fontSize: '1.1rem',
                        fontWeight: '700',
                        marginBottom: '15px',
                        color: colors.dark
                      }}
                      >
                        Description du poste
                      </h4>
                      <p style={{
                        fontSize: '0.95rem',
                        color: '#475569',
                        lineHeight: '1.7',
                        whiteSpace: 'pre-line'
                      }}
                      >
                        {job.description}
                      </p>
                    </div>

                    <div style={{
                      marginBottom: '20px'
                    }}
                    >
                      <h4 style={{
                        fontSize: '1.1rem',
                        fontWeight: '700',
                        marginBottom: '15px',
                        color: colors.dark
                      }}
                      >
                        Prérequis
                      </h4>
                      <ul style={{
                        paddingLeft: '20px',
                        marginTop: '10px'
                      }}
                      >
                        {job.requirements.map((req, index) => (
                          <li
                            key={index}
                            style={{
                              fontSize: '0.95rem',
                              color: '#475569',
                              marginBottom: '8px'
                            }}
                          >
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      style={{
                        backgroundColor: colors.secondary,
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '14px 28px',
                        fontWeight: '600',
                        fontSize: '1rem',
                        cursor: 'pointer',
                        boxShadow: '0 8px 15px rgba(255, 0, 110, 0.2)',
                        fontFamily,
                        display: 'inline-block',
                        marginTop: '10px'
                      }}
                      onClick={() => {
                        window.location.href = `mailto:contact@drivesoft.fr?subject=Candidature pour le poste de ${job.title}`;
                      }}
                    >
                      Postuler maintenant
                    </motion.button>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* SECTION POURQUOI NOUS REJOINDRE */}
      <motion.section
        style={{
          ...sectionStyle,
          backgroundColor: colors.darkGradient,
          color: colors.light
        }}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={fadeInUp}
      >
        <div style={{
          position: 'relative', zIndex: 1, maxWidth: '1000px', margin: '0 auto'
        }}
        >
          <motion.h2
            variants={itemVariant}
            style={{
              fontSize: 'clamp(2rem, 3vw, 2.5rem)',
              fontWeight: '800',
              color: colors.light,
              marginBottom: '20px',
              textAlign: 'center',
              position: 'relative',
              display: 'inline-block',
              width: '100%'
            }}
          >
            Pourquoi nous rejoindre
            <div style={{
              position: 'absolute',
              bottom: '-10px',
              left: '50%',
              width: '60px',
              height: '4px',
              background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
              borderRadius: '2px',
              transform: 'translateX(-50%)'
            }}
            />
          </motion.h2>

          <motion.p
            variants={itemVariant}
            style={{
              fontSize: 'clamp(1rem, 1.5vw, 1.2rem)',
              color: 'rgba(255, 255, 255, 0.8)',
              textAlign: 'center',
              maxWidth: '700px',
              margin: '0 auto 60px',
              lineHeight: '1.6',
            }}
          >
            Chez Drivesoft, nous valorisons nos équipes et nous nous efforçons de créer un environnement de travail épanouissant
          </motion.p>

          <div style={{ marginBottom: '60px' }}>
            <motion.h3
              variants={itemVariant}
              style={{
                fontSize: '1.4rem',
                fontWeight: '700',
                color: colors.light,
                marginBottom: '30px',
                textAlign: 'center'
              }}
            >
              Nos valeurs
            </motion.h3>

            <motion.div
              variants={staggerVariant}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '30px'
              }}
            >
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  variants={itemVariant}
                  style={{
                    flex: '1 1 200px',
                    maxWidth: '250px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    padding: '30px 25px',
                    borderRadius: '16px',
                    textAlign: 'center',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
                  }}
                  whileHover={{ y: -5 }}
                >
                  <h4 style={{
                    fontSize: '1.2rem',
                    fontWeight: '700',
                    marginBottom: '15px',
                    color: colors.light
                  }}
                  >
                    {value.title}
                  </h4>
                  <p style={{
                    fontSize: '0.95rem',
                    lineHeight: '1.7',
                    color: 'rgba(255, 255, 255, 0.8)'
                  }}
                  >
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>

          <div>
            <motion.h3
              variants={itemVariant}
              style={{
                fontSize: '1.4rem',
                fontWeight: '700',
                color: colors.light,
                marginBottom: '30px',
                textAlign: 'center'
              }}
            >
              Nos avantages
            </motion.h3>

            <motion.div
              variants={staggerVariant}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '15px'
              }}
            >
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  variants={itemVariant}
                  style={{
                    flex: '1 1 300px',
                    maxWidth: '450px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    backdropFilter: 'blur(5px)',
                    padding: '16px 20px',
                    borderRadius: '10px',
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px'
                  }}
                  whileHover={{ x: 5 }}
                >
                  <div style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: '700',
                    color: colors.primary,
                    flexShrink: 0
                  }}
                  >
                    {index + 1}
                  </div>
                  <p style={{
                    fontSize: '0.95rem',
                    color: 'rgba(255, 255, 255, 0.9)',
                    margin: 0
                  }}
                  >
                    {benefit}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* SECTION CONTACT */}
      <motion.section
        style={{
          ...sectionStyle,
          background: 'linear-gradient(135deg, #f8f9fa 0%, #f1f5f9 100%)',
          paddingTop: '80px'
        }}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={fadeInUp}
      >
        <div style={{
          position: 'relative', zIndex: 1, maxWidth: '700px', margin: '0 auto', textAlign: 'center'
        }}
        >
          <motion.h2
            variants={itemVariant}
            style={{
              fontSize: 'clamp(1.8rem, 3vw, 2.2rem)',
              fontWeight: '800',
              color: colors.dark,
              marginBottom: '20px'
            }}
          >
            Vous ne trouvez pas le poste qui vous convient ?
          </motion.h2>

          <motion.p
            variants={itemVariant}
            style={{
              fontSize: '1.1rem',
              color: '#64748b',
              lineHeight: '1.7',
              marginBottom: '40px'
            }}
          >
            Nous sommes toujours à la recherche de talents exceptionnels. Envoyez-nous votre CV et expliquez-nous pourquoi vous souhaitez rejoindre notre équipe.
          </motion.p>

          <motion.button
            variants={itemVariant}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            style={{
              backgroundColor: colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '16px 36px',
              fontWeight: '600',
              fontSize: '1.1rem',
              cursor: 'pointer',
              boxShadow: '0 10px 25px rgba(58, 134, 255, 0.3)',
              fontFamily
            }}
          >
            Candidature spontanée
          </motion.button>

          <motion.p
            variants={itemVariant}
            style={{
              fontSize: '0.95rem',
              color: '#64748b',
              marginTop: '30px'
            }}
          >
            Pour toute question concernant nos offres d'emploi, contactez-nous à <strong>recrutement@drivesoft.fr</strong>
          </motion.p>
        </div>
      </motion.section>
    </div>
  );
}

export default Recruitment;
